/*
 * feeling.js
 * Shared scripts for feeling-specific extensions of Flex 7.0.0.
 *
 * Loaded with `defer` after Flex's vendors.js, utilities.js and app.js
 * via snippets/feeling-head.liquid (feeling-hook 1, see docs/flex-hooks.md).
 *
 * Conventions:
 * - Everything lives in the window.Feeling namespace.
 * - Feature modules register themselves with Feeling.register(name, init)
 *   and are initialised once the DOM is ready.
 * - Use delegated event handlers; Flex re-renders parts of the DOM.
 */
(function () {
  'use strict';

  var Feeling = window.Feeling = window.Feeling || {};
  var modules = Feeling._modules = Feeling._modules || {};

  Feeling.version = '0.1.0';

  Feeling.register = function (name, init) {
    if (modules[name]) {
      console.warn('[feeling] module already registered: ' + name);
      return;
    }
    modules[name] = { init: init, initialised: false };
    if (Feeling._ready) {
      initModule(name);
    }
  };

  function initModule(name) {
    var module = modules[name];
    if (!module || module.initialised) return;
    module.initialised = true;
    try {
      module.init(Feeling);
    } catch (error) {
      console.error('[feeling] module failed to initialise: ' + name, error);
    }
  }

  function initAll() {
    Feeling._ready = true;
    Object.keys(modules).forEach(initModule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();

/*
 * Module "cartAdvisor" – mandatory cart attribute "Meine Beraterin" (cart stage 1, Migrationsmatrix W1).
 * Markup: snippets/feeling-cart-advisor.liquid (feeling-hook 5 in sections/cart__main.liquid).
 *
 * - Shows an own, accessible error message instead of the browser bubble (aria-invalid,
 *   aria-describedby, aria-live) and focuses/scrolls to the field.
 * - Values consisting only of spaces count as empty.
 * - Saves the value via /cart/update.js on change, so it survives page changes.
 * - Intercepts only the "checkout" submit of the cart form: validates, saves the attribute,
 *   verifies the saved value, then submits the form natively (POST /cart with "checkout").
 *   Save errors block the checkout with a message; repeated clicks are ignored while busy.
 * - The terms checkbox (native "required") and the order note are left untouched.
 */
(function () {
  'use strict';

  var ATTRIBUTE = 'Meine Beraterin';
  var SAVE_TIMEOUT_MS = 10000;

  function cartUpdateUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + 'cart/update.js';
  }

  function saveAttribute(value) {
    var attributes = {};
    attributes[ATTRIBUTE] = value;
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, SAVE_TIMEOUT_MS) : null;

    return fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (timer) clearTimeout(timer);
      if (!response.ok) throw new Error('cart/update.js HTTP ' + response.status);
      return response.json();
    }).then(function (cart) {
      var saved = cart && cart.attributes ? cart.attributes[ATTRIBUTE] : undefined;
      if ((saved || '') !== value) throw new Error('attribute not saved');
      return cart;
    }, function (error) {
      if (timer) clearTimeout(timer);
      throw error;
    });
  }

  function init() {
    var block = document.querySelector('[data-feeling-advisor]');
    if (!block) return;
    var input = block.querySelector('[data-feeling-advisor-input]');
    var errorEl = block.querySelector('[data-feeling-advisor-error]');
    var form = input ? input.form : null;
    if (!input || !errorEl || !form) return;

    var messageRequired = block.getAttribute('data-message-required') || '';
    var messageSaveError = block.getAttribute('data-message-save-error') || '';
    var busy = false;
    var lastSubmitter = null;
    var statusEl = null;

    function trimmed() {
      return (input.value || '').replace(/^\s+|\s+$/g, '');
    }

    function showError(message) {
      block.classList.add('feeling-advisor--invalid');
      input.setAttribute('aria-invalid', 'true');
      errorEl.textContent = message;
    }

    function clearError() {
      block.classList.remove('feeling-advisor--invalid');
      input.setAttribute('aria-invalid', 'false');
      errorEl.textContent = '';
    }

    function focusField() {
      // Deferred, so the browser's own focus handling of other invalid fields does not win.
      setTimeout(function () {
        if (input.scrollIntoView) input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      }, 0);
    }

    function statusElement() {
      if (statusEl) return statusEl;
      statusEl = document.createElement('p');
      statusEl.className = 'feeling-cart-status';
      statusEl.setAttribute('role', 'alert');
      var anchor = form.querySelector('.cart__buttons') || form.querySelector('[name="checkout"]');
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(statusEl, anchor.nextSibling);
      } else {
        block.appendChild(statusEl);
      }
      return statusEl;
    }

    function showStatus(message) {
      statusElement().textContent = message;
    }

    function clearStatus() {
      if (statusEl) statusEl.textContent = '';
    }

    function setBusy(state, submitter) {
      busy = state;
      form.classList.toggle('feeling-cart--busy', state);
      var button = submitter || form.querySelector('[name="checkout"]');
      if (button) {
        button.setAttribute('aria-busy', state ? 'true' : 'false');
        button.classList.toggle('is-loading', state);
      }
    }

    // Own message instead of the browser bubble (empty or only spaces -> required/pattern).
    input.addEventListener('invalid', function (event) {
      event.preventDefault();
      showError(messageRequired);
      focusField();
    });

    input.addEventListener('input', function () {
      if (trimmed() !== '') clearError();
      clearStatus();
    });

    // Keep the value in the cart even if the customer leaves the page (silent; errors are
    // handled again when the checkout is started).
    input.addEventListener('change', function () {
      saveAttribute(trimmed()).catch(function () {});
    });

    // Remember the clicked submit button for browsers without SubmitEvent.submitter.
    form.addEventListener('click', function (event) {
      var target = event.target && event.target.closest ? event.target.closest('button, input') : null;
      if (target && target.form === form && (target.type === 'submit' || target.type === 'image')) {
        lastSubmitter = target;
      }
    }, true);

    form.addEventListener('submit', function (event) {
      var submitter = event.submitter || lastSubmitter;
      lastSubmitter = null;
      if (!submitter || submitter.name !== 'checkout') return; // only the checkout is guarded

      event.preventDefault();
      if (busy) return; // ignore repeated clicks

      var value = trimmed();
      if (value === '') {
        showError(messageRequired);
        focusField();
        return;
      }

      clearError();
      clearStatus();
      input.value = value;
      setBusy(true, submitter);

      saveAttribute(value).then(function () {
        // Native submit keeps Shopify's own cart POST (note, attribute, checkout redirect).
        var marker = form.querySelector('input[type="hidden"][name="checkout"]');
        if (!marker) {
          marker = document.createElement('input');
          marker.type = 'hidden';
          marker.name = 'checkout';
          form.appendChild(marker);
        }
        marker.value = submitter.value || '';
        HTMLFormElement.prototype.submit.call(form);
      }).catch(function () {
        setBusy(false, submitter);
        showStatus(messageSaveError);
      });
    });

    // Back/forward cache: reset the busy state when the page is shown again.
    window.addEventListener('pageshow', function () {
      setBusy(false);
      var marker = form.querySelector('input[type="hidden"][name="checkout"]');
      if (marker) marker.parentNode.removeChild(marker);
    });
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('cartAdvisor', init);
  }
})();

/*
 * Module "productInfo" – variant updates for the product block "feeling" (plan step 2.3, P7/P8).
 * Markup: snippets/feeling-product-info.liquid (feeling-hooks 3/4).
 *
 * Flex 7 announces every variant change with the bubbling CustomEvent "variant-change" on the
 * product form (assets/kit-variant-picker-bridge.js; detail: product, variant, state). This module
 * listens on document, so it also works for the Quick Shop, whose markup is injected later.
 * Only elements of the same product inside the same product section are updated:
 * - [data-feeling-sku]:      SKU of the new variant; hidden while there is no SKU or no variant.
 * - [data-feeling-delivery]: shown only while the new variant is available.
 * - [data-feeling-notify]:   compact notify link, only for a sold-out variant, href ?variant=<id>.
 * Flex's own selectCallback/price UI stay untouched (no wrapping).
 */
(function () {
  'use strict';

  function init() {
    document.addEventListener('variant-change', function (event) {
      var form = event.target;
      var detail = event.detail || {};
      if (!form || !form.closest || !detail.product) return;

      var root = form.closest('.js-product_section') || form.closest('.product_section');
      if (!root) return;

      var productId = String(detail.product.id);
      var variant = detail.variant || null;
      var sku = variant && variant.sku ? String(variant.sku) : '';

      Array.prototype.forEach.call(root.querySelectorAll('[data-feeling-sku]'), function (el) {
        if (el.getAttribute('data-product-id') !== productId) return;
        var valueEl = el.querySelector('[data-feeling-sku-value]');
        if (valueEl) valueEl.textContent = sku;
        el.hidden = sku === '';
      });

      Array.prototype.forEach.call(root.querySelectorAll('[data-feeling-delivery]'), function (el) {
        if (el.getAttribute('data-product-id') !== productId) return;
        el.hidden = !(variant && variant.available);
      });

      // Compact notify link (Quick Shop): only for a sold-out variant, pointing to the product page
      // with exactly this variant (?variant=<id>); the full notify form stays on the product page.
      Array.prototype.forEach.call(root.querySelectorAll('[data-feeling-notify]'), function (el) {
        if (el.getAttribute('data-product-id') !== productId) return;
        var link = el.querySelector('[data-feeling-notify-link]');
        var soldOut = !!(variant && !variant.available && variant.id);
        if (link && soldOut) {
          link.setAttribute('href', el.getAttribute('data-base-url') + '?variant=' + encodeURIComponent(variant.id));
        }
        el.hidden = !soldOut;
      });
    });
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('productInfo', init);
  }
})();

/*
 * Module "soldOutSavings" - savings for unavailable variants (plan step 2.6, P9, V13).
 * Flex 2 showed compare-at price AND savings also for sold-out variants. Flex 7 renders the
 * compare-at price for them, but adds the savings line only for available variants
 * (z__jsProduct.js, price UI handler). This module adds the same line, in the same format
 * ("Sie sparen 20% (2,00 EUR)"), for unavailable variants.
 *
 * Flex re-renders the price UI (innerHTML) on every variant change and when the Quick Shop is
 * injected, so a MutationObserver re-applies the line after each render. Only acts when the
 * price block setting "display savings" is on (data-display-savings="true").
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    var translation = (window.PXUTheme && window.PXUTheme.translation) || {};
    if (cents === 0 && translation.free_price_text) return translation.free_price_text;
    var format = window.jQuery ? window.jQuery('body').data('money-format') : undefined;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    return (cents / 100).toFixed(2);
  }

  function apply(priceEl) {
    var block = priceEl.closest('.modal_price');
    var section = priceEl.closest('.js-product_section') || priceEl.closest('.product_section');
    if (!block || !section || block.getAttribute('data-display-savings') !== 'true') return;
    if (priceEl.querySelector('.feeling-savings, .savings')) return;

    var form = section.querySelector('[data-product-form]');
    if (!form || !window.kitVariantPicker || typeof window.kitVariantPicker.read !== 'function') return;
    var variant = window.kitVariantPicker.read(form).variant;
    if (!variant || variant.available || !(variant.compare_at_price > variant.price)) return;

    var translation = (window.PXUTheme && window.PXUTheme.translation) || {};
    var diff = variant.compare_at_price - variant.price;
    var percent = Math.round((diff * 100) / variant.compare_at_price);
    var span = document.createElement('span');
    span.className = 'sale savings feeling-savings';
    span.innerHTML = (translation.product_savings || '') + ' ' + percent + '% (<span class="money">' + formatMoney(diff) + '</span>)';
    priceEl.appendChild(span);
  }

  function scan() {
    var els = document.querySelectorAll('.product_section [data-price-ui]:not(.price-ui--loading)');
    Array.prototype.forEach.call(els, apply);
  }

  function init() {
    var scheduled = false;
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      // setTimeout instead of requestAnimationFrame: rAF is paused in background tabs.
      window.setTimeout(function () {
        scheduled = false;
        scan();
      }, 0);
    }
    scan();
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener('variant-change', schedule);
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('soldOutSavings', init);
  }
})();

/*
 * Module: productTabs
 * Product tabs "Beschreibung / Inhaltsstoffe / Steckbrief" (sections/feeling-product-tabs.liquid,
 * snippets/feeling-product-tabs.liquid; plan step 3.1). WAI-ARIA tabs pattern: the markup already
 * renders the first tab as active and hides the other panels, this module only switches.
 * Mouse: click on a tab. Keyboard: arrow left/right (wrapping), Home, End move the focus and
 * activate the tab; Tab moves from the active tab into its panel. Delegated on document, so it
 * also works for sections re-rendered by the theme editor. Deliberately not Flex's
 * PXUTheme.tabs (that one toggles every .tabs element on the page).
 */
(function () {
  'use strict';

  function tabsOf(root) {
    return Array.prototype.filter.call(root.querySelectorAll('[data-feeling-tab]'), function (tab) {
      return tab.closest('[data-feeling-tabs]') === root;
    });
  }

  function activate(root, target, moveFocus) {
    tabsOf(root).forEach(function (tab) {
      var selected = tab === target;
      var panel = document.getElementById(tab.getAttribute('aria-controls'));
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      if (panel) panel.hidden = !selected;
    });
    if (moveFocus) target.focus();
  }

  function init() {
    document.addEventListener('click', function (event) {
      var tab = event.target.closest && event.target.closest('[data-feeling-tab]');
      if (!tab) return;
      var root = tab.closest('[data-feeling-tabs]');
      if (root) activate(root, tab, false);
    });

    document.addEventListener('keydown', function (event) {
      var tab = event.target.closest && event.target.closest('[data-feeling-tab]');
      if (!tab) return;
      var root = tab.closest('[data-feeling-tabs]');
      if (!root) return;
      var tabs = tabsOf(root);
      var index = tabs.indexOf(tab);
      var next = null;
      switch (event.key) {
        case 'ArrowRight': next = tabs[(index + 1) % tabs.length]; break;
        case 'ArrowLeft': next = tabs[(index - 1 + tabs.length) % tabs.length]; break;
        case 'Home': next = tabs[0]; break;
        case 'End': next = tabs[tabs.length - 1]; break;
        default: return;
      }
      event.preventDefault();
      activate(root, next, true);
    });
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('productTabs', init);
  }
})();

/*
 * Module "cartTerms" – terms checkbox in the cart (Flex: input.tos_agree with native "required",
 * cart page #cart_agree and ajax cart #sliding_agree). Hanf acceptance test 2026-10-11.
 *
 * - The checkbox stays mandatory: the native "required" attribute and the browser validation
 *   are kept; only the browser bubble (large tooltip over the checkout button) is replaced.
 * - On "invalid": own message directly below the checkbox (role="alert", aria-describedby,
 *   aria-invalid), focus on the checkbox. Checking the box removes the message.
 * - Text: Flex locale key cart.general.agree_to_terms_warning, output by
 *   snippets/feeling-head.liquid as JSON (#feeling-i18n); fallback: the browser's own message.
 * - One capturing listener on document ("invalid" does not bubble), so re-rendered ajax cart
 *   markup is covered as well.
 */
(function () {
  'use strict';

  function texts() {
    var el = document.getElementById('feeling-i18n');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  function messageFor(input) {
    var id = 'feeling-terms-error-' + (input.id || 'cart');
    var el = document.getElementById(id);
    if (el) return el;
    el = document.createElement('p');
    el.id = id;
    el.className = 'feeling-terms-error';
    el.setAttribute('role', 'alert');
    var anchor = input.closest('label') || input.closest('.tos') || input;
    anchor.insertAdjacentElement('afterend', el);
    var describedBy = (input.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
    if (describedBy.indexOf(id) === -1) describedBy.push(id);
    input.setAttribute('aria-describedby', describedBy.join(' '));
    return el;
  }

  function init() {
    var message = texts().termsRequired;

    document.addEventListener('invalid', function (event) {
      var input = event.target;
      if (!input || !input.classList || !input.classList.contains('tos_agree')) return;
      event.preventDefault(); // replaces only the browser bubble; the submit stays blocked
      input.setAttribute('aria-invalid', 'true');
      messageFor(input).textContent = message || input.validationMessage || '';
      setTimeout(function () {
        if (input.scrollIntoView) input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      }, 0);
    }, true);

    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.classList || !input.classList.contains('tos_agree') || !input.checked) return;
      input.setAttribute('aria-invalid', 'false');
      var el = document.getElementById('feeling-terms-error-' + (input.id || 'cart'));
      if (el) el.textContent = '';
    });
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('cartTerms', init);
  }
})();

/*
 * Module "quickshopRating" – Trustoo rating stars in the quick shop (Hanf 2026-10-11,
 * reports/2026-10-11-hanf-trustoo-quickshop-klon.md).
 *
 * The Trustoo app block cannot be re-initialised in the dynamically loaded quick shop, so the
 * compact stars that Trustoo already rendered in the product card are cloned below the quick
 * shop title. No own rating logic, no metafields; the card stars stay untouched.
 *
 * Assignment chain (any mismatch -> no stars, never stars of another product):
 *   clicked span.js-quick-shop-link (data-id, data-url) -> its .product-wrap
 *   -> a.product-thumbnail__title with the same product handle
 *   -> its next sibling .vstar-star (inserted by Trustoo seal-review.min.js)
 *   -> product id in the opened quick shop ([data-section-data] product.id) equals data-id.
 * Only cloned when data-review-num > 0. If Trustoo has not inserted the stars yet, a
 * MutationObserver watches only this card's caption until they appear or the quick shop closes.
 * Uses the Fancybox events afterShow.fb / afterClose.fb of Flex' quick shop (utilities.js).
 */
(function () {
  'use strict';

  var trigger = null;
  var observer = null;

  function handleOf(url) {
    try {
      var match = new URL(url, window.location.origin).pathname.match(/\/products\/([^\/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function stopObserver() {
    if (observer) observer.disconnect();
    observer = null;
  }

  function cardTitle(button) {
    var card = button.closest('.product-wrap');
    var link = card && card.querySelector('a.product-thumbnail__title');
    var handle = handleOf(button.getAttribute('data-url') || '');
    if (!link || !handle || handleOf(link.getAttribute('href') || '') !== handle) return null;
    return link;
  }

  function cardStars(link) {
    var next = link.nextElementSibling;
    return next && next.classList.contains('vstar-star') ? next : null;
  }

  function openQuickshop() {
    return document.querySelector('.fancybox-is-open .js-quick-shop');
  }

  function quickshopProductId(quickshop) {
    var data = quickshop.querySelector('[data-section-data]');
    try { return String(JSON.parse(data.textContent).product.id); } catch (e) { return null; }
  }

  function insert(button, stars) {
    var quickshop = openQuickshop();
    if (!quickshop) return;
    var productId = String(button.getAttribute('data-id') || '');
    if (!productId || quickshopProductId(quickshop) !== productId) return;
    Array.prototype.forEach.call(quickshop.querySelectorAll('.feeling-qs-rating'), function (el) {
      el.remove();
    });
    if (!(parseInt(stars.getAttribute('data-review-num'), 10) > 0)) return;
    var title = quickshop.querySelector('.product-block--title');
    if (!title) return;
    var clone = stars.cloneNode(true);
    clone.removeAttribute('style');
    clone.classList.remove('tt-center', 'tt-right');
    var wrapper = document.createElement('div');
    wrapper.className = 'feeling-qs-rating';
    wrapper.setAttribute('data-product-id', productId);
    wrapper.appendChild(clone);
    title.insertAdjacentElement('afterend', wrapper);
  }

  function onShow() {
    var button = trigger;
    if (!button) return;
    stopObserver();
    try {
      var link = cardTitle(button);
      if (!link) return;
      var stars = cardStars(link);
      if (stars) {
        insert(button, stars);
        return;
      }
      if (!window.MutationObserver) return;
      // Trustoo has not rendered the card stars yet: watch only this card caption.
      observer = new MutationObserver(function () {
        var current = cardTitle(button);
        var late = current && cardStars(current);
        if (!late) return;
        stopObserver();
        if (trigger === button) insert(button, late);
      });
      observer.observe(link.parentElement, { childList: true });
    } catch (error) {
      stopObserver();
    }
  }

  function init() {
    var $ = window.jQuery;
    if (!$) return;

    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest && event.target.closest('.js-quick-shop-link');
      if (button) trigger = button;
    }, true);

    $(document).on('afterShow.fb', onShow);
    $(document).on('afterClose.fb', function () {
      stopObserver();
      trigger = null;
    });
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('quickshopRating', init);
  }
})();

/*
 * Module "cookieSettings" – footer menu link "#cookie-einstellungen" opens the native Shopify
 * cookie preferences dialog (Hanf 2026-10-12). The menu entry itself is maintained in the
 * Shopify navigation (link "#cookie-einstellungen"); no own consent UI, no beeClever.
 *
 * - Matches links whose hash is exactly "#cookie-einstellungen" on this shop's origin, in any
 *   output form ("#…", "/#…", absolute URL). Other links are not touched.
 * - Opens window.privacyBanner.showPreferences() (Shopify privacy banner, storefront-banner.js).
 *   If the banner script is not ready yet, it retries once after the page has loaded (no polling).
 * - One delegated click listener (module registry prevents double initialisation).
 */
(function () {
  'use strict';

  var HASH = '#cookie-einstellungen';

  function isSettingsLink(link) {
    var href = link.getAttribute('href');
    if (!href || href.indexOf(HASH) === -1) return false;
    try {
      var url = new URL(href, window.location.href);
      return url.hash === HASH && url.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  function showPreferences() {
    var banner = window.privacyBanner;
    if (banner && typeof banner.showPreferences === 'function') {
      banner.showPreferences();
      return true;
    }
    return false;
  }

  function openWhenReady() {
    if (showPreferences()) return;
    var retry = function () {
      if (!showPreferences()) console.warn('[feeling] Shopify cookie preferences are not available');
    };
    if (document.readyState === 'complete') {
      setTimeout(retry, 500);
    } else {
      window.addEventListener('load', retry, { once: true });
    }
  }

  function init() {
    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest && event.target.closest('a[href]');
      if (!link || !isSettingsLink(link)) return;
      event.preventDefault();
      openWhenReady();
    });
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('cookieSettings', init);
  }
})();

/*
 * Module "newTabLinks" – selected menu links open in a new tab (Hanf 2026-10-12: "feeling | Aromashop").
 * Flex 2 (feeling customisation in utilities.js) opened every external link in a new tab; Flex 7
 * keeps the default and only the URLs of the theme setting feeling_new_tab_urls are affected.
 *
 * - URLs come from #feeling-config (snippets/feeling-head.liquid), exact match after URL
 *   normalisation (protocol, host, path, query; trailing slash of the path ignored).
 * - Only menu links: desktop/vertical header navigation (.navbar-link) and mobile menu
 *   (.mobile-menu__item). Other links with the same address stay unchanged.
 * - Sets target="_blank" and rel="noopener noreferrer" on load and again right before a click
 *   (covers re-rendered menus); one delegated listener.
 */
(function () {
  'use strict';

  var MENU_LINKS = 'a.navbar-link, a.mobile-menu__item';

  function normalise(href) {
    try {
      var url = new URL(href, window.location.href);
      return url.origin + url.pathname.replace(/\/+$/, '') + url.search;
    } catch (e) {
      return null;
    }
  }

  function configuredUrls() {
    var el = document.getElementById('feeling-config');
    if (!el) return [];
    try {
      var list = (JSON.parse(el.textContent) || {}).newTabUrls || [];
      return list.map(normalise).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  function init() {
    var urls = configuredUrls();
    if (!urls.length) return;

    function apply(link) {
      if (!link.matches(MENU_LINKS)) return;
      if (urls.indexOf(normalise(link.getAttribute('href') || '')) === -1) return;
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }

    Array.prototype.forEach.call(document.querySelectorAll(MENU_LINKS), apply);

    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest && event.target.closest('a[href]');
      if (link) apply(link);
    }, true);
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('newTabLinks', init);
  }
})();

/*
 * Module "addressSearch" – search above the saved addresses in the customer account (K2, Hanf
 * 2026-10-13). Markup: snippets/feeling-address-search.liquid (feeling-hook 7 in
 * templates/customers/addresses.liquid), only when the setting feeling_address_search is on.
 *
 * - Searches only the visible address data of each card (.address-table__address: title with
 *   first/last name, .address-table__details with company, street, zip, city, province, country,
 *   phone), never the hidden edit forms.
 * - AND across all terms, case-insensitive; extra spaces are ignored; umlauts/accents match both
 *   ways ("Müller" is found by "muller" and "mueller", "Zürich" by "zurich").
 * - Only the addresses of the current page (Shopify pagination); the snippet shows a hint when
 *   there are more pages. Status for screen readers via aria-live.
 */
(function () {
  'use strict';

  var UMLAUTS = { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue' };

  function stripAccents(text) {
    return text.normalize ? text.normalize('NFD').replace(/[̀-ͯ]/g, '') : text;
  }

  // Two normalised forms: umlauts as digraphs (ae/oe/ue) and as base letters (a/o/u).
  function forms(text) {
    var lower = String(text || '').toLowerCase().replace(/ß/g, 'ss');
    return [
      stripAccents(lower.replace(/[äöü]/g, function (c) { return UMLAUTS[c]; })),
      stripAccents(lower)
    ];
  }

  function cardText(card) {
    var parts = [];
    var title = card.querySelector('.address-table__title');
    var details = card.querySelector('.address-table__details');
    if (title) parts.push(title.textContent);
    if (details) parts.push(details.textContent);
    return forms(parts.join(' ').replace(/\s+/g, ' ')).join(' ');
  }

  function init() {
    var root = document.querySelector('[data-feeling-address-search]');
    if (!root) return;
    var input = root.querySelector('[data-feeling-address-search-input]');
    var status = root.querySelector('[data-feeling-address-search-status]');
    var empty = root.querySelector('[data-feeling-address-search-empty]');
    var list = root.closest('.address-table') || document;
    var cards = Array.prototype.slice.call(list.querySelectorAll('.address-table__address'));
    var separators = Array.prototype.slice.call(list.querySelectorAll('hr'));
    var template = root.getAttribute('data-status-template') || '';
    var index = cards.map(cardText);

    function filter() {
      var terms = input.value.trim().split(/\s+/).filter(Boolean).map(forms);
      var shown = 0;
      cards.forEach(function (card, i) {
        var match = terms.every(function (term) {
          return index[i].indexOf(term[0]) !== -1 || index[i].indexOf(term[1]) !== -1;
        });
        card.hidden = !match;
        if (match) shown++;
      });
      // Flex inserts an <hr> after every third card; hide them while filtering.
      separators.forEach(function (hr) { hr.hidden = terms.length > 0; });
      if (empty) empty.hidden = shown !== 0;
      if (status) {
        status.textContent = terms.length
          ? template.replace('[count]', shown).replace('[total]', cards.length)
          : '';
      }
    }

    input.addEventListener('input', filter);
    input.addEventListener('search', filter); // clear button of type="search"
    if (input.value) filter();
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('addressSearch', init);
  }
})();

/*
 * Module "notifyPosted" – visible confirmation after the back-in-stock form (Hanf 2026-10-14,
 * reports/2026-10-14-hanf-flex7-verfuegbarkeit-erfolgsmeldung-fix.md).
 * Flex 7 writes the success text into .notify-form__success-message inside .product__notify-form
 * (z__jsProduct.js); selectCallback (utilities.js) hides that container as soon as an available
 * variant is selected. After the post Shopify reloads the product URL without ?variant=, so for
 * partly sold-out products the confirmation stayed invisible.
 *
 * - Only acts when Shopify confirmed the post (contact_posted=true) AND the redirect anchor is
 *   #notify_me (the form's own action) AND the page has form#notify_me. Other contact forms
 *   return with their own anchor and are not touched.
 * - Shows the existing translation PXUTheme.translation.notify_form_success
 *   (products.notify_form.post_success) in its own element directly before the container, so
 *   variant switching cannot hide it; role="status" for screen readers.
 * - Hides the Flex container (CSS class) to avoid a second, empty or duplicate message.
 * - No change to the form, its submission, the e-mail or Shopify's CAPTCHA flow.
 */
(function () {
  'use strict';

  function decode(text) {
    // The translation is HTML-escaped in snippets/js-variables.liquid.
    var doc = new DOMParser().parseFromString(String(text), 'text/html');
    return doc.documentElement.textContent;
  }

  function init() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('contact_posted') !== 'true' || window.location.hash !== '#notify_me') return;
    var form = document.querySelector('form#notify_me');
    var container = form && form.closest('.product__notify-form');
    var translation = window.PXUTheme && window.PXUTheme.translation;
    if (!container || !translation || !translation.notify_form_success) return;

    var message = document.createElement('p');
    message.className = 'feeling-notify-success card card-content';
    message.setAttribute('role', 'status');
    message.textContent = decode(translation.notify_form_success);
    container.parentNode.insertBefore(message, container);
    container.classList.add('feeling-notify--posted');
    message.scrollIntoView({ block: 'center' });
  }

  if (window.Feeling && window.Feeling.register) {
    window.Feeling.register('notifyPosted', init);
  }
})();
